import React, { useState } from "react";
import moment, { Moment } from "moment";

import {
  DateTimePicker,
  DateTimePickerProps,
} from "@mui/x-date-pickers/DateTimePicker";
import { TextFieldProps } from "@mui/material";

import { CalendarIcon } from "../../../design-system/icons";
import { dateTimeViewRenderers } from "../dateTimePickerViewRenderers";
import useDateTimePickerKeyboardCommit from "../hooks/useDateTimePickerKeyboardCommit";
import { ConfirmTimeDialog } from "./components";
import { useTimeFormat } from "globals/hooks";

export type MoovsDateTimePickerProps = {
  onAccept: (date: any) => void;
  renderInputProps?: TextFieldProps;
  clearable?: boolean;
  name?: string;
  inputFormat?: string;
  hideTabs?: boolean;
  [key: string]: any;
};

function MoovsDateTimePickerWithConfirm(props: MoovsDateTimePickerProps) {
  const {
    renderInputProps = {},
    clearable = false,
    name,
    onAccept,
    inputFormat,
    hideTabs,
    value,
    ...otherProps
  } = props;

  // hooks
  const { is24Hour } = useTimeFormat();

  // state
  const [openDateTimePicker, setOpenDateTimePicker] = useState(false);
  const [dateTime, setDateTime] = useState<Moment | null>(null);
  const [openConfirmTimeDialog, setOpenConfirmTimeDialog] = useState(false);

  const committedValue = value ? moment(value) : null;

  // event handlers
  const commitAcceptedValue = (acceptedDateTime: Moment | null) => {
    if (!acceptedDateTime) {
      setOpenDateTimePicker(false);
    } else {
      setDateTime(acceptedDateTime);

      // onAccept triggers onClose, thus we need to set DatePicker open when clicking OK
      setOpenDateTimePicker(true);

      const dateTimeMomentObj = moment(acceptedDateTime);
      const hour = dateTimeMomentObj.hour();
      const minute = dateTimeMomentObj.minute();

      // Open ConfirmTimeDialog if selected time is between 12 AM and 8AM
      if (
        (hour === 0 && minute >= 0) ||
        (hour === 8 && minute < 1) ||
        (hour > 0 && hour < 8)
      ) {
        setOpenConfirmTimeDialog(true);
      } else {
        onAccept(acceptedDateTime);
        setOpenDateTimePicker(false);
      }
    }
  };

  const {
    handleBlur,
    handleKeyDown,
    markActionBarInteraction,
    recordValidation,
    resetCommitState,
  } = useDateTimePickerKeyboardCommit({
    committedValue,
    draftValue: dateTime,
    onCommit: commitAcceptedValue,
    onBlur: renderInputProps.onBlur,
    onKeyDown: renderInputProps.onKeyDown,
  });

  const handleDateTimePickerOpen = () => {
    if (openDateTimePicker) {
      return;
    }

    resetCommitState();
    setDateTime(committedValue);
    setOpenDateTimePicker(true);
  };

  const handleDateTimePickerChange: NonNullable<
    DateTimePickerProps<Moment>["onChange"]
  > = (date, context) => {
    recordValidation(context);
    setDateTime(date ? moment(date) : null);
  };

  const handleDateTimePickerAccept = (acceptedDateTime: Moment | null) => {
    commitAcceptedValue(acceptedDateTime ? moment(acceptedDateTime) : null);
  };

  const handleDateTimePickerClose = () => {
    resetCommitState();
    setOpenDateTimePicker(false);
  };

  const {
    onBlur: _onBlur,
    onKeyDown: _onKeyDown,
    ...textFieldProps
  } = renderInputProps;

  const dateTimeNow = moment(new Date());
  const useAmPm = !is24Hour;

  return (
    <>
      <DateTimePicker
        {...otherProps}
        value={openDateTimePicker ? dateTime : committedValue}
        open={openDateTimePicker}
        ampm={useAmPm}
        format={inputFormat}
        onChange={handleDateTimePickerChange}
        onAccept={handleDateTimePickerAccept}
        onClose={handleDateTimePickerClose}
        minDateTime={dateTimeNow}
        // complete date-only selections with the current time instead of
        // midnight (the default, from the clock views' day granularity)
        referenceDate={dateTimeNow}
        slots={{ openPickerIcon: CalendarIcon }}
        viewRenderers={dateTimeViewRenderers}
        timeSteps={{ minutes: 1 }}
        slotProps={{
          actionBar: {
            actions: clearable ? ["clear", "cancel", "accept"] : ["cancel", "accept"],
            onMouseDownCapture: markActionBarInteraction,
          },
          tabs: { hidden: hideTabs },
          textField: {
            name,
            onClick: handleDateTimePickerOpen,
            variant: "outlined",
            fullWidth: true,
            inputProps: {
              style: { cursor: "pointer" },
            },
            ...textFieldProps,
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
          },
        }}
      />
      <ConfirmTimeDialog
        openConfirmTimeDialog={openConfirmTimeDialog}
        setOpenConfirmTimeDialog={setOpenConfirmTimeDialog}
        dateTime={dateTime}
        onAccept={onAccept}
        setOpenDateTimePicker={setOpenDateTimePicker}
      />
    </>
  );
}
export default MoovsDateTimePickerWithConfirm;
